import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { EcommerceService } from 'src/app/services/ecommerce.service';
import { ToastrService } from 'ngx-toastr';
import {
  getClientDiagnosticEventTypeBadgeClass,
  getClientDiagnosticEventTypeLabel,
  isChargedNoOrderEvent,
  isClientDiagnosticErrorEvent
} from '../pos-diagnostics-event-types';

@Component({
  selector: 'app-pos-diagnostics-session',
  templateUrl: './pos-diagnostics-session.component.html',
  styleUrls: ['./pos-diagnostics-session.component.scss']
})
export class PosDiagnosticsSessionComponent implements OnInit, OnChanges {

  @Input() storeId: string = '';
  @Input() initialSessionId: string = '';

  sessionId = '';
  events: any[] = [];
  isLoading = false;
  errorMessage = '';
  endpointUnavailable = false;
  sessionMeta: any = null;
  private loadSeq = 0;

  constructor(
    private ecommerceService: EcommerceService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    if (this.initialSessionId) {
      this.sessionId = this.initialSessionId;
      this.loadSession();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['storeId'] && !changes['storeId'].firstChange) {
      this.resetForStoreChange();
      if (this.sessionId?.trim() && this.storeId) {
        this.loadSession();
      }
      return;
    }

    if (changes['initialSessionId']?.currentValue && !changes['initialSessionId']?.firstChange) {
      this.sessionId = changes['initialSessionId'].currentValue;
      this.loadSession();
    }
  }

  private resetForStoreChange(): void {
    this.loadSeq++;
    this.events = [];
    this.sessionMeta = null;
    this.errorMessage = '';
    this.endpointUnavailable = false;
  }

  loadSession(): void {
    const id = this.sessionId?.trim();
    if (!id || !this.storeId) {
      return;
    }

    const seq = ++this.loadSeq;
    const requestedStoreId = this.storeId;
    const requestedSessionId = id;

    this.isLoading = true;
    this.errorMessage = '';
    this.endpointUnavailable = false;
    this.events = [];
    this.sessionMeta = null;

    this.ecommerceService.getClientDiagnosticSession(requestedStoreId, requestedSessionId).subscribe({
      next: (res: any) => {
        if (seq !== this.loadSeq || requestedStoreId !== this.storeId || requestedSessionId !== this.sessionId?.trim()) {
          return;
        }
        const data = res?.response?.data ?? res?.data ?? {};
        this.sessionMeta = data;
        const rawEvents = data.events || data.timeline || [];
        this.events = [...rawEvents].sort((a: any, b: any) =>
          new Date(a.occurredAt || a.createdAt).getTime() - new Date(b.occurredAt || b.createdAt).getTime()
        );
        this.isLoading = false;
      },
      error: (error: any) => {
        if (seq !== this.loadSeq || requestedStoreId !== this.storeId || requestedSessionId !== this.sessionId?.trim()) {
          return;
        }
        this.isLoading = false;
        if (error?.status === 404) {
          this.endpointUnavailable = true;
          this.errorMessage = 'Session timeline API is not deployed yet.';
        } else {
          this.errorMessage = error?.error?.response?.message || 'Failed to load session timeline';
          this.toastr.error(this.errorMessage);
        }
      }
    });
  }

  getLevelBadgeClass(level: string): string {
    switch (level) {
      case 'error': return 'bg-danger';
      case 'warn': return 'bg-warning text-dark';
      case 'info': return 'bg-info text-dark';
      default: return 'bg-secondary';
    }
  }

  isErrorEvent(event: any): boolean {
    return isClientDiagnosticErrorEvent(event);
  }

  isCriticalFinancialEvent(event: any): boolean {
    return isChargedNoOrderEvent(event?.eventType);
  }

  getEventTypeLabel(eventType: string): string {
    return getClientDiagnosticEventTypeLabel(eventType);
  }

  getEventTypeBadgeClass(eventType: string): string {
    return getClientDiagnosticEventTypeBadgeClass(eventType);
  }

  getBreadcrumbIcon(kind: string): string {
    switch (kind) {
      case 'nav': return 'navigation-2-outline';
      case 'api': return 'globe-outline';
      case 'action': return 'flash-outline';
      case 'state': return 'layers-outline';
      default: return 'radio-button-on-outline';
    }
  }

  trackByEventId(index: number, event: any): string {
    return event._id || event.id || String(index);
  }

  hasErrors(): boolean {
    return this.events.some(e => e.level === 'error');
  }
}
